import React, { useEffect, useMemo, useState } from "react";
import { validateStartTimeRanges } from "../../../helpers/modelBuilder/departureTimeConfig";
import GranularDepartureRangesEditor from "./GranularDepartureRangesEditor";
import TypeFilteredDropdown from "./TypeFilteredDropdown";
import "./MBConfigTab.css";

export default function MBConfigTab({
  configVariables = [],
  activeVariableName = "",
  activeNodeLayerId = "",
  activeVisibilityLinksLayerId = "",
  nodeLayerOptions = [],
  linksLayerOptions = [],
  hazardLayerOptions = [],
  allowHazardConfig = false,
  isEditingDestinations = false,
  isEditingStartTimes = false,
  isEditingHazardConfig = false,
  isEditingContraflow = false,
  isEditingBlockedRoad = false,
  blockedRoadTwoSided = true,
  isVariableSelectionLocked = false,
  isSaving = false,
  saveError = "",
  saveSuccess = "",
  onSelectVariable,
  onCreateVariable,
  onDeleteVariable,
  onChangeNodeLayer,
  onChangeVisibilityLinksLayer,
  onStartEditDestinations,
  onStartEditStartTimes,
  onStartEditHazardConfig,
  onStartEditContraflow,
  onChangeContraflowLinksLayer,
  onChangeContraflowDestinationConfig,
  onSwitchContraflowLink,
  onClearContraflow,
  onRemoveContraflowEntry,
  onChangeContraflowLaneAllocation,
  onFocusContraflowLink,
  onAddContraflowLinkIds,
  onStartEditBlockedRoad,
  onChangeBlockedRoadLinksLayer,
  onChangeBlockedRoadTwoSided,
  onClearBlockedRoad,
  onRemoveBlockedRoadEntry,
  onFocusBlockedRoadLink,
  onAddBlockedRoadLinkIds,
  hazardConfigName = "",
  onChangeHazardConfigName,
  onCancelEdit,
  onClearDestinations,
  onRemoveDestination,
  onFocusDestinationNode,
  onChangeDepartureMin,
  onChangeDepartureMax,
  onChangeGranularSurvey,
  onAddStartTimeRange,
  onUpdateStartTimeRange,
  onRemoveStartTimeRange,
  onChangeHazardLayer,
  onChangeHazardRerouting,
  onAddHazardEntry,
  onRemoveHazardEntry,
  onAddDestinationIds,
  onSaveConfig,
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newVarName, setNewVarName] = useState("");
  const [newVarType, setNewVarType] = useState("destination");
  const [destinationIdsInput, setDestinationIdsInput] = useState("");
  const [linkIdsInput, setLinkIdsInput] = useState("");
  const [addingLinkIds, setAddingLinkIds] = useState(false);

  useEffect(() => {
    setLinkIdsInput("");
  }, [activeVariableName, isEditingBlockedRoad, isEditingContraflow]);

  useEffect(() => {
    if (!allowHazardConfig && newVarType === "hazard_layer") setNewVarType("destination");
  }, [allowHazardConfig, newVarType]);

  const activeVariable = useMemo(() => {
    if (!configVariables.length) return null;
    return (
      configVariables.find((row) => String(row.name) === String(activeVariableName)) ||
      configVariables[0] ||
      null
    );
  }, [configVariables, activeVariableName]);

  const variableLabel = (row) => row.name === "destinations"
    ? "destinations (default)"
    : row.name === "departure_times"
      ? "departure_times (default)"
      : `${row.name} (${row.type || "destination"})`;

  const activeType = String(activeVariable?.type || "destination");
  const isDestinationConfig = activeType === "destination";
  const isStartTimesConfig = activeType === "start_times";
  const isHazardConfig = activeType === "hazard_layer";
  const isContraflowConfig = activeType === "contraflow";
  const isBlockedRoadConfig = activeType === "blocked_road";

  const destinations = Array.isArray(activeVariable?.destinations) ? activeVariable.destinations : [];
  const departureMinValue =
    activeVariable?.minDepartureTime == null ? "" : String(activeVariable.minDepartureTime);
  const departureMaxValue =
    activeVariable?.maxDepartureTime == null ? "" : String(activeVariable.maxDepartureTime);
  const granularSurvey = Boolean(activeVariable?.granularSurvey);
  const startTimeRanges = Array.isArray(activeVariable?.startTimeRanges)
    ? activeVariable.startTimeRanges
    : [];
  const granularValidation = validateStartTimeRanges(startTimeRanges);
  const granularTotalPct = startTimeRanges.reduce(
    (sum, range) => sum + (Number(range?.probabilityWeightPct) || 0),
    0
  );

  const handleCreate = async () => {
    const ok = await onCreateVariable?.({
      name: newVarName,
      type: newVarType,
    });
    if (!ok) return;
    handleCancelCreate();
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewVarName("");
    setNewVarType("destination");
  };

  const handleAddDestinationIds = async () => {
    const raw = String(destinationIdsInput || "").trim();
    if (!raw) return;
    const ok = await onAddDestinationIds?.(raw);
    if (ok) setDestinationIdsInput("");
  };

  const handleAddLinkIds = async (onAdd) => {
    const raw = linkIdsInput.trim();
    if (!raw || addingLinkIds) return;
    setAddingLinkIds(true);
    try {
      if (await onAdd?.(raw)) setLinkIdsInput("");
    } finally {
      setAddingLinkIds(false);
    }
  };
  const linkIdsEditor = (onAdd) => (
    <div className="mb-config-inlineInputRow">
      <input
        className="mb-config-input"
        type="text"
        value={linkIdsInput}
        onChange={(event) => setLinkIdsInput(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); handleAddLinkIds(onAdd); } }}
        placeholder="Add link IDs (comma-separated)"
        aria-label="Link IDs to add"
        disabled={isSaving || addingLinkIds}
      />
      <button className="mb-btn" type="button" onClick={() => handleAddLinkIds(onAdd)} disabled={!linkIdsInput.trim() || isSaving || addingLinkIds}>
        {addingLinkIds ? "Adding..." : "Add IDs"}
      </button>
    </div>
  );

  return (
    <div className="mb-config-shell">
      <div className="mb-config-headerRow">
        <div className="mb-config-heading">Simulation Config</div>
        {!showCreateForm ? (
          <div className="mb-config-actions">
            <button
              className="mb-btn mb-btnSecondary"
              type="button"
              onClick={() => setShowCreateForm(true)}
              disabled={isSaving || isVariableSelectionLocked}
            >
              Create
            </button>
          </div>
        ) : null}
      </div>

      {showCreateForm ? (
        <>
          <div className="mb-config-form">
            <div className="mb-config-formRow">
              <label className="mb-config-label">Variable Name</label>
              <input
                className="mb-config-input"
                type="text"
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                placeholder={newVarType === "hazard_layer" ? "hazard_layers" : "dest_2"}
                disabled={isSaving}
              />
            </div>
            <div className="mb-config-formRow">
              <label className="mb-config-label">Type</label>
              <select
                className="mb-config-select"
                value={newVarType}
                onChange={(e) => setNewVarType(e.target.value)}
                disabled={isSaving}
              >
                <option value="destination">destination</option>
                <option value="start_times">start_times</option>
                {allowHazardConfig && <option value="hazard_layer">hazard_layer</option>}
                <option value="contraflow">Contraflow</option>
                <option value="blocked_road">Blocked roads</option>
              </select>
            </div>
          </div>

          <div className="mb-config-actions">
            <button className="mb-btn" type="button" onClick={handleCreate} disabled={isSaving}>
              Save
            </button>
            <button
              className="mb-btn mb-btnSecondary"
              type="button"
              onClick={handleCancelCreate}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
          {saveError ? <div className="mb-config-msg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-config-msg isOk">{saveSuccess}</div> : null}
        </>
      ) : (
        <>
          <div className="mb-config-field">
            <span className="mb-config-label">Variable</span>
            <TypeFilteredDropdown
              groupLabel="Variables"
              selectedId={activeVariable?.name || ""}
              selectedLabel={activeVariable ? variableLabel(activeVariable) : "No variables"}
              emptyLabel="No variables match the selected types."
              types={[
                { value: "destination", label: "Destinations" },
                { value: "start_times", label: "Start times" },
                ...(allowHazardConfig ? [{ value: "hazard_layer", label: "Hazard config" }] : []),
                { value: "contraflow", label: "Contraflow" },
                { value: "blocked_road", label: "Blocked roads" },
              ]}
              items={configVariables.map((row) => ({
                id: row.name,
                type: row.type || "destination",
                label: variableLabel(row),
              }))}
              onSelect={onSelectVariable}
              disabled={isSaving || isVariableSelectionLocked}
            />
          </div>

          {isDestinationConfig ? (
            <>
              <div className="mb-config-field">
                <label className="mb-config-label">Node Layer</label>
                <select
                  className="mb-config-select"
                  value={activeNodeLayerId || ""}
                  onChange={(e) => onChangeNodeLayer?.(e.target.value)}
                  disabled={!activeVariable || !nodeLayerOptions.length || isSaving || !isEditingDestinations}
                >
                  {!nodeLayerOptions.length ? (
                    <option value="">No node layers</option>
                  ) : (
                    nodeLayerOptions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                        {row.filename ? ` (${row.filename})` : ""}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="mb-config-field">
                <label className="mb-config-label">Links Layer (for visibility)</label>
                <select
                  className="mb-config-select"
                  value={activeVisibilityLinksLayerId || ""}
                  onChange={(e) => onChangeVisibilityLinksLayer?.(e.target.value)}
                  disabled={!activeVariable || isSaving || !isEditingDestinations}
                >
                  <option value="">(None)</option>
                  {linksLayerOptions.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.name}
                      {row.filename ? ` (${row.filename})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-config-field">
                <label className="mb-config-label">Destinations</label>

                <div className="mb-config-inputLike">
                  {destinations.length ? (
                    <div className="mb-config-chipRow">
                      {destinations.map((id) => (
                        <span key={id} className="mb-config-chip">
                          <button className="mb-config-pillLink" type="button" onClick={() => onFocusDestinationNode?.(id)} aria-label={`Zoom to destination node N${id}`}>N{id}</button>
                          {isEditingDestinations && (
                            <button
                              className="mb-config-chipAction"
                              type="button"
                              onClick={() => onRemoveDestination?.(id)}
                              disabled={isSaving}
                              aria-label={`Remove destination N${id}`}
                              title={`Remove destination N${id}`}
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="mb-config-placeholder">No destinations selected</span>
                  )}
                </div>

                {isEditingDestinations ? (
                  <div className="mb-config-inlineInputRow">
                    <input
                      className="mb-config-input"
                      type="text"
                      value={destinationIdsInput}
                      onChange={(e) => setDestinationIdsInput(e.target.value)}
                      placeholder="Add node IDs (comma-separated)"
                      disabled={isSaving}
                    />
                    <button
                      className="mb-btn"
                      type="button"
                      onClick={handleAddDestinationIds}
                      disabled={!destinationIdsInput.trim() || isSaving}
                    >
                      Add IDs
                    </button>
                  </div>
                ) : null}

                {isEditingDestinations ? (
                  <div className="mb-config-help">
                    Destination selection mode is active. Click nodes on the map to add/remove destinations.
                  </div>
                ) : null}

                <div className="mb-config-actions">
                  {!isEditingDestinations ? (
                    <>
                      <button className="mb-btn" type="button" onClick={onStartEditDestinations}>Edit Destinations</button>
                      {activeVariable?.name !== "destinations" && <button className="mb-btn mb-config-deleteButton" type="button" onClick={() => onDeleteVariable?.(activeVariable.name)} disabled={isSaving}>Delete config</button>}
                    </>
                  ) : (
                    <>
                      <button
                        className="mb-btn mb-btnSecondary"
                        type="button"
                        onClick={onCancelEdit}
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        className="mb-btn mb-btnSecondary"
                        type="button"
                        onClick={onClearDestinations}
                        disabled={!destinations.length || isSaving}
                      >
                        Clear
                      </button>
                      <button className="mb-btn" type="button" onClick={onSaveConfig} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : null}

          {isStartTimesConfig ? (
            <>
              {!granularSurvey ? (
                <>
                  <div className="mb-config-field">
                    <label className="mb-config-label">departure_min (in minutes)</label>
                    <input
                      className="mb-config-input"
                      type="number"
                      min="0"
                      step="1"
                      value={departureMinValue}
                      onChange={(e) => onChangeDepartureMin?.(e.target.value)}
                      disabled={isSaving || !isEditingStartTimes}
                    />
                  </div>

                  <div className="mb-config-field">
                    <label className="mb-config-label">departure_max (in minutes)</label>
                    <input
                      className="mb-config-input"
                      type="number"
                      min="0"
                      step="1"
                      value={departureMaxValue}
                      onChange={(e) => onChangeDepartureMax?.(e.target.value)}
                      disabled={isSaving || !isEditingStartTimes}
                    />
                  </div>
                </>
              ) : null}

              <div className="mb-config-field">
                <label className="mb-config-label">total population</label>
                <input
                  className="mb-config-input"
                  type="text"
                  value={granularSurvey ? `${granularTotalPct}% / 100%` : "100%"}
                  disabled
                />
              </div>

              <div className="mb-config-field">
                <label className="mb-config-checkRow">
                  <input
                    type="checkbox"
                    checked={granularSurvey}
                    onChange={(e) => onChangeGranularSurvey?.(e.target.checked)}
                    disabled={isSaving || !isEditingStartTimes}
                  />
                  <span>granular survey</span>
                </label>
              </div>

              {granularSurvey ? (
                <GranularDepartureRangesEditor
                  ranges={startTimeRanges}
                  isEditing={isEditingStartTimes}
                  isSaving={isSaving}
                  onAddRange={onAddStartTimeRange}
                  onUpdateRange={onUpdateStartTimeRange}
                  onRemoveRange={onRemoveStartTimeRange}
                />
              ) : null}

              <div className="mb-config-actions">
                {!isEditingStartTimes ? (
                  <button className="mb-btn" type="button" onClick={onStartEditStartTimes}>
                    Edit Departure Times
                  </button>
                ) : (
                  <>
                    <button
                      className="mb-btn mb-btnSecondary"
                      type="button"
                      onClick={onCancelEdit}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className="mb-btn"
                      type="button"
                      onClick={onSaveConfig}
                      disabled={isSaving || (granularSurvey && !granularValidation.valid)}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  </>
                )}
              </div>
            </>
          ) : null}

          {isBlockedRoadConfig ? (
            <>
              <div className="mb-config-field">
                <label className="mb-config-label">Link Layer</label>
                <select className="mb-config-select" value={String(activeVariable?.linksLayerId || "")}
                  onChange={(event) => onChangeBlockedRoadLinksLayer?.(event.target.value)} disabled={isSaving || !isEditingBlockedRoad}>
                  {!linksLayerOptions.length ? <option value="">No link layers</option> : null}
                  {linksLayerOptions.map((layer) => <option key={layer.id} value={layer.id}>{layer.name}{layer.filename ? ` (${layer.filename})` : ""}</option>)}
                </select>
              </div>
              <div className="mb-config-field">
                <label><input type="checkbox" checked={blockedRoadTwoSided} onChange={(event) => onChangeBlockedRoadTwoSided?.(event.target.checked)} disabled={!isEditingBlockedRoad || isSaving} /> Two-sided block</label>
                <div className="mb-config-help">Click a link to {blockedRoadTwoSided ? "block both directions when available" : "block only that direction"}.</div>
              </div>
              <div className="mb-config-field">
                <label className="mb-config-label">Blocked links</label>
                {(activeVariable?.blockedRoadEntries || []).length ? (
                  <div className="mb-config-chipRow">
                    {activeVariable.blockedRoadEntries.map((entry) => <span className="mb-config-chip" key={entry.linkId}>
                      <button className="mb-config-pillLink" type="button" onClick={() => onFocusBlockedRoadLink?.(entry.linkId)}>L{entry.linkId}</button>
                      {isEditingBlockedRoad && <button className="mb-config-chipAction" type="button" onClick={() => onRemoveBlockedRoadEntry?.(entry.linkId)} disabled={isSaving} aria-label={`Remove blocked link L${entry.linkId}`}>×</button>}
                    </span>)}
                  </div>
                ) : <div className="mb-config-inputLike"><span className="mb-config-placeholder">No links selected</span></div>}
                {isEditingBlockedRoad ? linkIdsEditor(onAddBlockedRoadLinkIds) : null}
                <div className="mb-config-actions">
                  {!isEditingBlockedRoad ? <><button className="mb-btn" type="button" onClick={onStartEditBlockedRoad}>Edit</button><button className="mb-btn mb-config-deleteButton" type="button" onClick={() => onDeleteVariable?.(activeVariable.name)} disabled={isSaving}>Delete</button></> : <>
                    <button className="mb-btn mb-btnSecondary" type="button" onClick={onCancelEdit} disabled={isSaving}>Cancel</button>
                    <button className="mb-btn mb-btnSecondary" type="button" onClick={onClearBlockedRoad} disabled={isSaving || !(activeVariable?.blockedRoadEntries || []).length}>Clear</button>
                    <button className="mb-btn" type="button" onClick={onSaveConfig} disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</button>
                  </>}
                </div>
              </div>
            </>
          ) : null}

          {isContraflowConfig ? (
            <>
              <div className="mb-config-field">
                <label className="mb-config-label">Link Layer</label>
                <select
                  className="mb-config-select"
                  value={String(activeVariable?.linksLayerId || "")}
                  onChange={(e) => onChangeContraflowLinksLayer?.(e.target.value)}
                  disabled={isSaving || !isEditingContraflow}
                >
                  {!linksLayerOptions.length ? <option value="">No link layers</option> : null}
                  {linksLayerOptions.map((row) => (
                    <option key={row.id} value={row.id}>{row.name}{row.filename ? ` (${row.filename})` : ""}</option>
                  ))}
                </select>
              </div>
              <div className="mb-config-field">
                <label className="mb-config-label">Destination Config</label>
                <select
                  className="mb-config-select"
                  value={String(activeVariable?.destinationConfigName || "")}
                  onChange={(e) => onChangeContraflowDestinationConfig?.(e.target.value)}
                  disabled={isSaving || !isEditingContraflow}
                >
                  {configVariables.filter((row) => row.type === "destination").map((row) => (
                    <option key={row.name} value={row.name}>{row.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-config-field">
                <label className="mb-config-label">Contraflow Links</label>
                {(activeVariable?.contraflowEntries || []).length ? (
                  <div className="mb-config-contraflowTable">
                    <div className="mb-config-contraflowHeader">Main link</div>
                    <div className="mb-config-contraflowHeader">Direction</div>
                    <div className="mb-config-contraflowHeader">Default</div>
                    <div className="mb-config-contraflowHeader">New allocation</div>
                    <div className="mb-config-contraflowHeader">Actions</div>
                    {(activeVariable.contraflowEntries || []).map((entry) => {
                      const laneDataAvailable = Number.isInteger(entry.mainLanes) &&
                        entry.mainLanes >= 0 &&
                        Number.isInteger(entry.reverseLanes) &&
                        entry.reverseLanes >= 0;
                      const total = laneDataAvailable ? entry.mainLanes + entry.reverseLanes : 0;
                      const minimumMain = Number.isInteger(entry.originalMainLanes)
                        ? entry.originalMainLanes
                        : 0;
                      const allocations = laneDataAvailable
                        ? Array.from({ length: Math.max(0, total - minimumMain + 1) }, (_, index) => total - index)
                        : [];
                      return (
                        <React.Fragment key={entry.linkId}>
                          <div>
                            <button
                              className="mb-config-linkButton"
                              type="button"
                              onClick={() => onFocusContraflowLink?.(entry.linkId)}
                              title={`Show link ${entry.linkId} on map`}
                            >
                              L{entry.linkId}
                            </button>
                            <div className="mb-config-subtle">reverse: L{entry.reverseLinkId || "—"}</div>
                          </div>
                          <div>{entry.startNodeId || "?"} → {entry.endNodeId || "?"}</div>
                          <div>{entry.originalMainLanes ?? "?"}–{entry.originalReverseLanes ?? "?"}</div>
                          <div>
                            {laneDataAvailable ? (
                              <select
                                className="mb-config-select"
                                value={entry.mainLanes}
                                onChange={(event) => onChangeContraflowLaneAllocation?.(entry.linkId, event.target.value)}
                                disabled={!isEditingContraflow || isSaving}
                              >
                                {allocations.map((mainCount) => (
                                  <option key={mainCount} value={mainCount}>{mainCount}–{total - mainCount}</option>
                                ))}
                              </select>
                            ) : <span className="mb-config-placeholder">Lane data unavailable</span>}
                          </div>
                          <div className="mb-config-rowActions">
                            <button className="mb-btn mb-btnSecondary" type="button" onClick={() => onSwitchContraflowLink?.(entry.linkId)} disabled={!isEditingContraflow || isSaving || !entry.reverseLinkId}>Flip</button>
                            <button className="mb-btn mb-btnDanger" type="button" onClick={() => onRemoveContraflowEntry?.(entry.linkId)} disabled={!isEditingContraflow || isSaving}>Remove</button>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : <div className="mb-config-inputLike"><span className="mb-config-placeholder">No links selected</span></div>}
                {isEditingContraflow ? linkIdsEditor(onAddContraflowLinkIds) : null}
                {isEditingContraflow ? <div className="mb-config-help">Click links on the map to add/remove them. Use ↔ to switch a chosen link to its reverse direction.</div> : null}
                <div className="mb-config-actions">
                  {!isEditingContraflow ? (
                    <><button className="mb-btn" type="button" onClick={onStartEditContraflow}>Edit</button><button className="mb-btn mb-config-deleteButton" type="button" onClick={() => onDeleteVariable?.(activeVariable.name)} disabled={isSaving}>Delete</button></>
                  ) : <>
                    <button className="mb-btn mb-btnSecondary" type="button" onClick={onCancelEdit} disabled={isSaving}>Cancel</button>
                    <button className="mb-btn mb-btnSecondary" type="button" onClick={onClearContraflow} disabled={isSaving || !(activeVariable?.contraflowEntries || []).length}>Clear</button>
                    <button className="mb-btn" type="button" onClick={onSaveConfig} disabled={isSaving}>{isSaving ? "Saving..." : "Save"}</button>
                  </>}
                </div>
              </div>
            </>
          ) : null}

          {isHazardConfig ? (
            <>
              <div className="mb-config-field">
                <label className="mb-config-label">Hazard Config Name</label>
                <input
                  className="mb-config-input"
                  type="text"
                  value={isEditingHazardConfig ? hazardConfigName : (activeVariable?.name || "")}
                  onChange={(e) => onChangeHazardConfigName?.(e.target.value)}
                  disabled={isSaving || !isEditingHazardConfig}
                />
              </div>
              {(activeVariable?.hazardEntries || []).map((entry, index) => (
                <div className="mb-config-form" key={`${entry.name || "hazard"}_${index}`}>
                  <div className="mb-config-formRow">
                    <label className="mb-config-label">{entry.name || `hazard_layer_${index + 1}`}</label>
                    <select
                      className="mb-config-select"
                      value={String(entry.hazardLayerId || "")}
                      onChange={(e) => onChangeHazardLayer?.(index, e.target.value)}
                      disabled={isSaving || !isEditingHazardConfig || !hazardLayerOptions.length}
                    >
                      {!hazardLayerOptions.length ? <option value="">No hazard layers</option> : null}
                      {hazardLayerOptions.map((row) => (
                        <option key={row.id} value={row.id}>{row.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-config-formRow">
                    <label className="mb-config-checkRow">
                      <input
                        type="checkbox"
                        checked={Boolean(entry.hazardRerouting)}
                        onChange={(e) => onChangeHazardRerouting?.(index, e.target.checked)}
                        disabled={isSaving || !isEditingHazardConfig}
                      />
                      <span>hazardRerouting</span>
                    </label>
                    <button
                      className="mb-btn mb-btnSecondary"
                      type="button"
                      onClick={() => onRemoveHazardEntry?.(index)}
                      disabled={isSaving || !isEditingHazardConfig}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <div className="mb-config-actions">
                {!isEditingHazardConfig ? (
                  <button className="mb-btn" type="button" onClick={onStartEditHazardConfig} disabled={isSaving}>Edit Hazard Config</button>
                ) : <>
                  <button className="mb-btn mb-btnSecondary" type="button" onClick={onAddHazardEntry} disabled={isSaving || !hazardLayerOptions.length}>Add Hazard Layer</button>
                  <button className="mb-btn mb-btnSecondary" type="button" onClick={onCancelEdit} disabled={isSaving}>Cancel</button>
                  <button className="mb-btn" type="button" onClick={onSaveConfig} disabled={isSaving || !hazardConfigName.trim() || !(activeVariable?.hazardEntries || []).length}>{isSaving ? "Saving..." : "Save"}</button>
                </>}
              </div>
            </>
          ) : null}

          {saveError ? <div className="mb-config-msg isError">{saveError}</div> : null}
          {saveSuccess ? <div className="mb-config-msg isOk">{saveSuccess}</div> : null}
        </>
      )}
    </div>
  );
}
